import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  getCompanyAdvisorsStats,
  getCompanyDiagnostics,
  getCompanyRevenue,
  createAdvisor,
  suspendAdvisor,
  reactivateAdvisor,
  reassignDiagnostics,
  AdvisorStatsRow,
  CompanyDiagnosticRow,
  CompanyRevenueResult,
} from '../../services/adminService';
import { dpsTheme } from '../../styles/dpsTheme';
import { useDeviceSize } from '../../hooks/useMediaQuery';

const PAGE_SIZE = 10;

// ── Modal générique ───────────────────────────────────────────────────────────
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'rgb(15,23,42)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: '16px', padding: '32px', width: '480px', maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: dpsTheme.colors.text.muted, padding: '4px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', borderRadius: '8px',
  border: `1px solid ${dpsTheme.colors.border.primary}`,
  background: 'rgba(30,41,59,0.8)', color: dpsTheme.colors.text.primary,
  fontSize: dpsTheme.typography.fontSize.sm, boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 24px', borderRadius: '8px', border: 'none',
  background: dpsTheme.colors.accent.blue.gradient, color: 'white',
  fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold,
  cursor: 'pointer',
};

function formatEuros(n: number): string {
  if (n === 0) return '0 €';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}

// ── Score circle ──────────────────────────────────────────────────────────────
function ScoreCircle({ score }: { score: number }) {
  const color  = score < 35 ? '#ef4444' : score < 65 ? '#f59e0b' : '#10b981';
  const bg     = score < 35 ? 'rgba(239,68,68,0.15)' : score < 65 ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)';
  const border = score < 35 ? 'rgba(239,68,68,0.5)'  : score < 65 ? 'rgba(245,158,11,0.5)'  : 'rgba(16,185,129,0.5)';
  return (
    <div style={{
      width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
      background: bg, border: `2px solid ${border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: '11px', color, letterSpacing: '-0.5px',
    }}>
      {score}
    </div>
  );
}

// ── Statut client ─────────────────────────────────────────────────────────────
function ClientBadge({ clientId, lastSignIn }: { clientId: string | null; lastSignIn: string | null }) {
  if (!clientId) return null;
  if (lastSignIn) {
    return (
      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', whiteSpace: 'nowrap' }}>
        Client actif
      </span>
    );
  }
  return (
    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)', whiteSpace: 'nowrap' }}>
      En attente
    </span>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export function CompanyAdminDashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useDeviceSize();
  const companyId = userProfile?.company_id ?? '';

  const [advisors,       setAdvisors]       = useState<AdvisorStatsRow[]>([]);
  const [diagnostics,    setDiagnostics]    = useState<CompanyDiagnosticRow[]>([]);
  const [companyRevenue, setCompanyRevenue] = useState<CompanyRevenueResult>({ total_actions_completed: 0, total_revenue: 0 });
  const [loading,        setLoading]        = useState(true);

  // Tri & pagination diagnostics
  const [sortMode, setSortMode] = useState<'recent' | 'score'>('recent');
  const [page,     setPage]     = useState(0);

  // Modal création conseiller
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating,        setCreating]        = useState(false);
  const [form,            setForm]            = useState({ email: '', firstName: '', lastName: '' });

  // Modal réaffectation
  const [reassignFrom, setReassignFrom] = useState<AdvisorStatsRow | null>(null);
  const [reassignTo,   setReassignTo]   = useState('');
  const [reassigning,  setReassigning]  = useState(false);

  const loadData = async () => {
    if (!companyId) return;
    try {
      const [adv, diags, revenue] = await Promise.all([
        getCompanyAdvisorsStats(companyId),
        getCompanyDiagnostics(companyId),
        getCompanyRevenue(companyId),
      ]);
      setAdvisors(adv);
      setDiagnostics(diags);
      setCompanyRevenue(revenue);
    } catch (e: any) {
      toast.error(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [companyId]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCreateAdvisor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.firstName || !form.lastName) { toast.error('Tous les champs sont requis'); return; }
    try {
      setCreating(true);
      await createAdvisor(form.email, form.firstName, form.lastName, companyId);
      toast.success('Invitation envoyée — le conseiller recevra un email pour définir son mot de passe');
      setShowCreateModal(false);
      setForm({ email: '', firstName: '', lastName: '' });
      await loadData();
    } catch (e: any) {
      toast.error(e.message || 'Erreur création conseiller');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleSuspend = async (advisor: AdminUserProfile) => {
    try {
      if (advisor.is_active) { await suspendAdvisor(advisor.id);    toast.success(`${advisor.first_name} suspendu`); }
      else                   { await reactivateAdvisor(advisor.id); toast.success(`${advisor.first_name} réactivé`); }
      await loadData();
    } catch (e: any) { toast.error(e.message || 'Erreur'); }
  };

  const handleReassign = async () => {
    if (!reassignFrom || !reassignTo) return;
    try {
      setReassigning(true);
      await reassignDiagnostics(reassignFrom.id, reassignTo);
      toast.success('Diagnostics réaffectés');
      setReassignFrom(null); setReassignTo('');
      await loadData();
    } catch (e: any) {
      toast.error(e.message || 'Erreur réaffectation');
    } finally {
      setReassigning(false);
    }
  };

  // ── Stats dérivées ─────────────────────────────────────────────────────────
  const activeAdvisors   = advisors.filter(a => a.is_active);
  const activeClientCount = useMemo(() => new Set(
    diagnostics.filter(d => d.client_id && d.client_last_sign_in).map(d => d.client_id)
  ).size, [diagnostics]);

  // ── Tri diagnostics (côté client) ─────────────────────────────────────────
  const sortedDiagnostics = useMemo(() => {
    const copy = [...diagnostics];
    if (sortMode === 'score') copy.sort((a, b) => a.security_score - b.security_score);
    // 'recent' : déjà trié par created_at DESC depuis la DB
    return copy;
  }, [diagnostics, sortMode]);

  const totalPages = Math.ceil(sortedDiagnostics.length / PAGE_SIZE);
  const pageDiags  = sortedDiagnostics.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSortChange = (mode: 'recent' | 'score') => {
    setSortMode(mode);
    setPage(0);
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: dpsTheme.colors.background.primary, paddingTop: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: dpsTheme.colors.text.muted }}>Chargement...</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: dpsTheme.typography.fontFamily, minHeight: '100vh', background: dpsTheme.colors.background.primary, paddingTop: '64px', color: dpsTheme.colors.text.secondary }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? `${dpsTheme.spacing.lg} ${dpsTheme.spacing.md}` : dpsTheme.spacing['3xl'] }}>

        {/* ── Header ── */}
        <div style={{ marginBottom: dpsTheme.spacing['2xl'] }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', borderRadius: '20px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', marginBottom: '12px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
            <span style={{ fontSize: dpsTheme.typography.fontSize.xs, fontWeight: dpsTheme.typography.fontWeight.semibold, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Admin</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h1 style={{ fontSize: isMobile ? dpsTheme.typography.fontSize['2xl'] : dpsTheme.typography.fontSize['4xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary, marginBottom: '8px' }}>
                {userProfile?.company_name}
              </h1>
              <p style={{ color: dpsTheme.colors.text.muted }}>Gestion des conseillers et des diagnostics</p>
            </div>
          </div>
        </div>

        {/* ── Stats (6 cartes) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: dpsTheme.spacing.lg, marginBottom: dpsTheme.spacing['2xl'] }}>
          {[
            { label: 'Conseillers actifs',  value: activeAdvisors.length,                                   green: false },
            { label: 'Conseillers total',   value: advisors.length,                                         green: false },
            { label: 'Diagnostics total',   value: diagnostics.length,                                      green: false },
            { label: 'Clients actifs',      value: activeClientCount,                                       green: false },
            { label: 'Actions réalisées',   value: companyRevenue.total_actions_completed,                  green: true  },
            { label: 'CA total généré',     value: formatEuros(companyRevenue.total_revenue),               green: true  },
          ].map(stat => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${stat.green ? 'rgba(16,185,129,0.25)' : dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing.xl, textAlign: 'center' }}>
              <p style={{ fontSize: dpsTheme.typography.fontSize['3xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: stat.green ? '#10b981' : dpsTheme.colors.text.primary }}>{stat.value}</p>
              <p style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted, marginTop: '4px' }}>{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* ── Section conseillers ── */}
        <div style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing['2xl'], marginBottom: dpsTheme.spacing['2xl'] }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: dpsTheme.spacing.xl }}>
            <h2 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>
              Conseillers ({advisors.length})
            </h2>
            <button onClick={() => setShowCreateModal(true)} style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Nouveau conseiller
            </button>
          </div>

          {advisors.length === 0 ? (
            <p style={{ color: dpsTheme.colors.text.muted, textAlign: 'center', padding: '32px' }}>Aucun conseiller — créez le premier</p>
          ) : isMobile ? (
            /* ── Vue mobile : cartes empilées ── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {advisors.map(advisor => (
                <div key={advisor.id} style={{ background: dpsTheme.colors.background.darker, border: `1px solid ${dpsTheme.colors.border.secondary}`, borderRadius: dpsTheme.borderRadius.md, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <p style={{ fontWeight: 600, color: dpsTheme.colors.text.primary, fontSize: dpsTheme.typography.fontSize.sm }}>{advisor.first_name} {advisor.last_name}</p>
                      <p style={{ fontSize: '11px', color: dpsTheme.colors.text.muted }}>{advisor.email}</p>
                    </div>
                    <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: advisor.is_active ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: advisor.is_active ? '#10b981' : '#ef4444', border: `1px solid ${advisor.is_active ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                      {advisor.is_active ? 'Actif' : 'Suspendu'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                    <span style={{ color: dpsTheme.colors.text.muted }}>{advisor.total_diagnostics} diag.</span>
                    <span style={{ color: advisor.total_actions_completed > 0 ? '#10b981' : dpsTheme.colors.text.muted }}>{advisor.total_actions_completed} actions</span>
                    <span style={{ color: advisor.total_revenue > 0 ? '#10b981' : dpsTheme.colors.text.muted, fontWeight: advisor.total_revenue > 0 ? 700 : 400 }}>{formatEuros(advisor.total_revenue)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {!advisor.is_active && (
                      <button onClick={() => { setReassignFrom(advisor); setReassignTo(''); }} style={{ padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 500, cursor: 'pointer', border: '1px solid rgba(251,191,36,0.3)', background: 'rgba(251,191,36,0.1)', color: '#fbbf24' }}>Réaffecter</button>
                    )}
                    <button onClick={() => handleToggleSuspend(advisor)} style={{ padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 500, cursor: 'pointer', border: `1px solid ${advisor.is_active ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`, background: advisor.is_active ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', color: advisor.is_active ? '#ef4444' : '#10b981' }}>
                      {advisor.is_active ? 'Suspendre' : 'Réactiver'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* ── Vue desktop : tableau ── */
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${dpsTheme.colors.border.secondary}` }}>
                    {['Conseiller', 'Statut', 'Diagnostics', 'Actions réalisées', 'CA généré', ''].map(col => (
                      <th key={col} style={{ padding: '8px 12px', textAlign: col === '' ? 'right' : 'left', fontSize: '11px', fontWeight: 600, color: dpsTheme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {advisors.map((advisor, idx) => (
                    <tr key={advisor.id} style={{ borderBottom: idx < advisors.length - 1 ? `1px solid ${dpsTheme.colors.border.secondary}` : 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      {/* Conseiller */}
                      <td style={{ padding: '12px' }}>
                        <p style={{ fontWeight: 600, color: dpsTheme.colors.text.primary, fontSize: dpsTheme.typography.fontSize.sm }}>{advisor.first_name} {advisor.last_name}</p>
                        <p style={{ fontSize: '11px', color: dpsTheme.colors.text.muted }}>{advisor.email}</p>
                      </td>
                      {/* Statut */}
                      <td style={{ padding: '12px' }}>
                        <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: advisor.is_active ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: advisor.is_active ? '#10b981' : '#ef4444', border: `1px solid ${advisor.is_active ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                          {advisor.is_active ? 'Actif' : 'Suspendu'}
                        </span>
                      </td>
                      {/* Diagnostics */}
                      <td style={{ padding: '12px', fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.secondary, textAlign: 'left' }}>
                        {advisor.total_diagnostics}
                      </td>
                      {/* Actions réalisées */}
                      <td style={{ padding: '12px', textAlign: 'left' }}>
                        <span style={{ fontSize: dpsTheme.typography.fontSize.sm, fontWeight: advisor.total_actions_completed > 0 ? 600 : 400, color: advisor.total_actions_completed > 0 ? '#10b981' : dpsTheme.colors.text.muted }}>
                          {advisor.total_actions_completed}
                        </span>
                      </td>
                      {/* CA généré */}
                      <td style={{ padding: '12px', textAlign: 'left' }}>
                        <span style={{ fontSize: dpsTheme.typography.fontSize.sm, fontWeight: advisor.total_revenue > 0 ? 700 : 400, color: advisor.total_revenue > 0 ? '#10b981' : dpsTheme.colors.text.muted }}>
                          {formatEuros(advisor.total_revenue)}
                        </span>
                      </td>
                      {/* Actions */}
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          {!advisor.is_active && (
                            <button onClick={() => { setReassignFrom(advisor); setReassignTo(''); }} style={{ padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 500, cursor: 'pointer', border: '1px solid rgba(251,191,36,0.3)', background: 'rgba(251,191,36,0.1)', color: '#fbbf24', whiteSpace: 'nowrap' }}>Réaffecter</button>
                          )}
                          <button onClick={() => handleToggleSuspend(advisor)} style={{ padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 500, cursor: 'pointer', border: `1px solid ${advisor.is_active ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`, background: advisor.is_active ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', color: advisor.is_active ? '#ef4444' : '#10b981', whiteSpace: 'nowrap' }}>
                            {advisor.is_active ? 'Suspendre' : 'Réactiver'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Section diagnostics ── */}
        <div style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing['2xl'] }}>

          {/* En-tête avec boutons de tri */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: dpsTheme.spacing.xl }}>
            <h2 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>
              Diagnostics ({diagnostics.length})
            </h2>
            <div style={{ display: 'flex', gap: '6px' }}>
              {(['recent', 'score'] as const).map(mode => {
                const label = mode === 'recent' ? 'Les plus récents' : 'Scores les plus bas';
                const active = sortMode === mode;
                return (
                  <button key={mode} onClick={() => handleSortChange(mode)} style={{
                    padding: '6px 14px', borderRadius: '20px', fontSize: dpsTheme.typography.fontSize.xs,
                    fontWeight: active ? 600 : 400, cursor: 'pointer', transition: 'all 0.15s',
                    border: active ? '1px solid rgba(59,130,246,0.5)' : `1px solid ${dpsTheme.colors.border.secondary}`,
                    background: active ? 'rgba(59,130,246,0.15)' : 'transparent',
                    color: active ? '#60a5fa' : dpsTheme.colors.text.muted,
                  }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Liste */}
          {diagnostics.length === 0 ? (
            <p style={{ color: dpsTheme.colors.text.muted, textAlign: 'center', padding: '32px' }}>Aucun diagnostic</p>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pageDiags.map(diag => {
                  const advisorName = [diag.advisor_first_name, diag.advisor_last_name].filter(Boolean).join(' ') || null;
                  return (
                    <div key={diag.id} style={{
                      background: dpsTheme.colors.background.darker,
                      border: `1px solid ${dpsTheme.colors.border.secondary}`,
                      borderRadius: dpsTheme.borderRadius.md,
                      padding: isMobile ? '12px 14px' : '12px 16px',
                      display: 'flex', alignItems: 'center',
                      gap: '12px', flexWrap: isMobile ? 'wrap' : 'nowrap',
                    }}>

                      {/* Score */}
                      <ScoreCircle score={diag.security_score} />

                      {/* Commune + date */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary, fontSize: dpsTheme.typography.fontSize.sm, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {diag.diagnostic_data?.city ?? 'Sans localisation'}
                        </p>
                        <p style={{ fontSize: '11px', color: dpsTheme.colors.text.muted }}>
                          {new Date(diag.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>

                      {/* Référence client */}
                      {diag.client_reference && (
                        <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 500, background: 'rgba(148,163,184,0.1)', color: dpsTheme.colors.text.muted, border: `1px solid ${dpsTheme.colors.border.secondary}`, whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {diag.client_reference}
                        </span>
                      )}

                      {/* Nom du conseiller */}
                      {advisorName && (
                        <span style={{ fontSize: '11px', color: dpsTheme.colors.text.muted, whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {advisorName}
                        </span>
                      )}

                      {/* Statut client */}
                      <ClientBadge clientId={diag.client_id} lastSignIn={diag.client_last_sign_in} />

                      {/* Bouton Voir */}
                      <button
                        onClick={() => navigate(`/dps/report/${diag.id}`)}
                        style={{ padding: '5px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', flexShrink: 0, border: `1px solid ${dpsTheme.colors.border.primary}`, background: 'rgba(255,255,255,0.05)', color: dpsTheme.colors.text.secondary, transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#60a5fa'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = dpsTheme.colors.border.primary; e.currentTarget.style.color = dpsTheme.colors.text.secondary; }}
                      >
                        Voir
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', gap: '12px' }}>
                  <span style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted }}>
                    Page {page + 1} sur {totalPages} · {diagnostics.length} diagnostics
                  </span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      style={{ padding: '6px 14px', borderRadius: '6px', fontSize: dpsTheme.typography.fontSize.xs, cursor: page === 0 ? 'default' : 'pointer', border: `1px solid ${dpsTheme.colors.border.secondary}`, background: 'transparent', color: page === 0 ? dpsTheme.colors.text.muted : dpsTheme.colors.text.secondary, opacity: page === 0 ? 0.4 : 1 }}
                    >
                      ← Précédent
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page === totalPages - 1}
                      style={{ padding: '6px 14px', borderRadius: '6px', fontSize: dpsTheme.typography.fontSize.xs, cursor: page === totalPages - 1 ? 'default' : 'pointer', border: `1px solid ${dpsTheme.colors.border.secondary}`, background: 'transparent', color: page === totalPages - 1 ? dpsTheme.colors.text.muted : dpsTheme.colors.text.secondary, opacity: page === totalPages - 1 ? 0.4 : 1 }}
                    >
                      Suivant →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Modal création conseiller ── */}
      {showCreateModal && (
        <Modal title="Nouveau conseiller" onClose={() => setShowCreateModal(false)}>
          <form onSubmit={handleCreateAdvisor} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '6px' }}>Prénom</label>
                <input style={inputStyle} value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} placeholder="Jean" required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '6px' }}>Nom</label>
                <input style={inputStyle} value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} placeholder="Dupont" required />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '6px' }}>Email</label>
              <input style={inputStyle} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jean.dupont@example.com" required />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button type="button" onClick={() => setShowCreateModal(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`, background: 'transparent', color: dpsTheme.colors.text.secondary, cursor: 'pointer', fontSize: dpsTheme.typography.fontSize.sm }}>Annuler</button>
              <button type="submit" disabled={creating} style={{ ...btnPrimary, opacity: creating ? 0.6 : 1 }}>{creating ? 'Envoi...' : "Envoyer l'invitation"}</button>
            </div>
            <p style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, textAlign: 'center', marginTop: '4px' }}>
              Un email d'invitation sera envoyé à cette adresse.
            </p>
          </form>
        </Modal>
      )}

      {/* ── Modal réaffectation ── */}
      {reassignFrom && (
        <Modal title={`Réaffecter les diagnostics de ${reassignFrom.first_name}`} onClose={() => { setReassignFrom(null); setReassignTo(''); }}>
          <p style={{ color: dpsTheme.colors.text.muted, fontSize: dpsTheme.typography.fontSize.sm, marginBottom: '20px' }}>
            Choisir le conseiller qui reprend les diagnostics de {reassignFrom.first_name} {reassignFrom.last_name}.
          </p>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '6px' }}>Conseiller cible</label>
            <select value={reassignTo} onChange={e => setReassignTo(e.target.value)} style={{ ...inputStyle, appearance: 'none' }}>
              <option value="">— Sélectionner —</option>
              {advisors.filter((a): a is AdvisorStatsRow => a.id !== reassignFrom.id && a.is_active).map(a => (
                <option key={a.id} value={a.id}>{a.first_name} {a.last_name} ({a.email})</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={() => { setReassignFrom(null); setReassignTo(''); }} style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`, background: 'transparent', color: dpsTheme.colors.text.secondary, cursor: 'pointer', fontSize: dpsTheme.typography.fontSize.sm }}>Annuler</button>
            <button onClick={handleReassign} disabled={!reassignTo || reassigning} style={{ ...btnPrimary, opacity: (!reassignTo || reassigning) ? 0.6 : 1 }}>
              {reassigning ? 'Réaffectation...' : 'Confirmer'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
